<template>
  <div class="flex flex-col justify-between flex-1">
    <div class="divide-y divide-gray-200">
      <div class="pt-6 pb-5">
        <component
          :is="is_any_nested_modal_open ? 'form' : 'Form'"
          v-slot="{ values, errors }"
          :v-if="!is_any_nested_modal_open"
          :initial-values="initial_values"
          novalidate
          :validation-schema="schema"
          class="relative mt-2 space-y-4"
          @submit="createPlan">
          <div class="absolute flex right-2 top-2" :class="[`${fullScreen ? '-mt-20' : '-mt-20'}`]">
            <div :class="[{ '!mt-6': inPlaybook }]" class="flex items-center mt-0 mr-6 gap-x-2">
              <Switch
                v-model="auto_compute_status"
                :class="[
                  auto_compute_status ? 'bg-blue' : 'bg-gray-200',
                  'relative inline-flex flex-shrink-0 h-7 w-12 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
                ]">
                <span
                  aria-hidden="true"
                  :class="[
                    auto_compute_status && $store.state.portfolio.ui_options?.language == 'ar'
                      ? '-translate-x-5'
                      : auto_compute_status
                        ? 'translate-x-5'
                        : 'translate-x-0',
                    'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                  ]" />
              </Switch>
              <span class="dark:text-common-grey">{{ $t("Auto Compute Status") }}</span>
            </div>
            <div
              v-if="!inPlaybook"
              class="flex items-center justify-center w-8 h-8 bg-white border rounded-full cursor-pointer has-tooltip dark:bg-dark-screen-background dark:border-dark-divider-border"
              @click="handleExpandPlan(values)">
              <Maximize2 class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" color="#2DA7D7" />
              <span
                class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                {{ fullScreen ? $t("Minimize") : $t("Full Screen") }}</span>
            </div>
            <div
              v-if="!inPlaybook"
              class="flex items-center justify-center w-8 h-8 ml-2 bg-white border rounded-full cursor-pointer has-tooltip dark:border-dark-divider-border dark:bg-dark-screen-background"
              @click="handleClosePlan">
              <X color="#FF0000" class="w-6 h-6 transition ease-in-out duration-side-menu-transition-duration" /><span
                class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                {{ $t("Close") }}</span>
            </div>
          </div>
          <div class="w-full">
            <SelectTemplateDropDownMenu
              v-if="templates?.length"
              v-model="selected_template"
              :items="templates"
              label-attr="name"
              class="relative"
              :is-box-background="true"
              input-classes="sm:text-sm appearance-none h-10 font-medium text-gray-700 text-left block w-full px-3 py-2 w-48 rounded-lg shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light "
              @isEditTemplate="
                ($event) => {
                  is_edit_template = $event
                }
              " />
          </div>
          <TextInput
            :auto-focus="true"
            autocomplete="on"
            :autocomplete-options="resultReferencesListForText"
            name="name"
            type="text"
            :label="$t('Plan name')"
            :value="
              selected_template?.content?.name && is_edit_template
                ? selected_template.name
                : selected_template?.content?.name
                  ? $t('Copy of') + ` ${selected_template.content.name}`
                  : ''
            "
            input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
            @update:modelValue="
              ($event) => {
                plan_name = $event
              }
            " />
          <div class="mt-5 border rounded-md dark:border-dark-divider-border h-28">
            <TextEditor
              ref="quill"
              v-model:content="plan_description"
              name="description"
              :label="$t('Description')"
              content-type="html"
              input-classes="rounded-md"
              :value="selected_template?.content?.description"
              theme="bubble"
              toolbar="essential" />
          </div>
          <div class="flex flex-row items-center justify-between gap-2">
            <SelectInput
              name="type"
              :items="types"
              label-attr="label"
              :allow-null="false"
              :classes="'w-full'"
              value-attr="value"
              :label="$t('Plan type')"
              :show-label="true"
              :is-box-background="true"
              :value="selected_template?.content?.type"
              input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
              @update:currentItem="
                ($event) => {
                  plan_type = $event
                }
              " />

            <div class="relative w-full">
              <label
                for="start_date"
                class="absolute z-10 block px-1 text-sm transition-all ease-in-out -translate-y-1/2 rounded-full h-fit dark:text-dark-search-bar-text dark:text-dark-body-text left-2 duration-side-menu-transition-duration"
                :class="[
                  inputStartDateIsFocused || !!start_date ? 'top-0' : 'top-1/2',
                  isBoxBackground
                    ? 'bg-light-box-background dark:bg-dark-box-background'
                    : 'bg-white text-gray-400 dark:bg-dark-box-background',
                  inputStartDateIsFocused ? 'text-light-brand-blue dark:text-dark-brand-blue' : ''
                ]">{{ $t("Start date") }}</label>
              <div class="rounded-md shadow-sm">
                <DatePicker
                  ref="start_date"
                  v-model="start_date"
                  :is-dark="$store.state.ui.darkMode"
                  class="bg-white dark:bg-dark-screen-background"
                  is-inline
                  mode="date"
                  :popover="{ visibility: 'click' }">
                  <template #default="{ inputValue, inputEvents }">
                    <div class="relative">
                      <svg
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        class="absolute w-4 h-full mx-2 text-gray-600 pointer-events-none">
                        <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>

                      <input
                        id="start_date"
                        :value="inputValue"
                        autocomplete="off"
                        class="w-full h-10 px-10 pl-8 pr-2 transition ease-in-out border rounded-lg outline-none text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue duration-side-menu-transition-duration"
                        :class="[
                          !isBoxBackground
                            ? 'bg-light-box-background dark:bg-dark-box-background'
                            : 'bg-light-screen-background dark:bg-dark-box-background'
                        ]"
                        @focusin="focusStartDateInput"
                        @focusout="focusStartDateInput"
                        v-on="inputEvents"
                        @input="
                          $utils.debounce(() => {
                            start_date = $event.target?.value
                          }, 800)
                        ">
                    </div>
                  </template>
                  <template #footer>
                    <div class="flex items-center justify-end pr-2 w-full pb-2 mt-0">
                      <button v-if="start_date" type="button" class="cancel-btn inline-block" @click.stop="emptyStartDate">
                        {{ $t("Clear") }}
                      </button>
                      <button type="button" class="cancel-btn inline-block" @click.stop="clearDate">{{ $t("Cancel") }}</button>
                    </div>
                  </template>
                </DatePicker>
              </div>
            </div>
            <div class="relative w-full">
              <label
                for="create_plan_d_date"
                class="absolute z-10 block px-1 text-sm transition-all ease-in-out -translate-y-1/2 rounded-full h-fit dark:text-dark-search-bar-text dark:text-dark-body-text left-2 duration-side-menu-transition-duration"
                :class="[
                  !!relative_date_text || inputDueDateIsFocused || !!due_date ? 'top-0' : 'top-1/2',
                  isBoxBackground
                    ? 'bg-light-box-background dark:bg-dark-box-background'
                    : 'bg-white text-gray-400 dark:bg-dark-box-background',
                  inputDueDateIsFocused ? 'text-light-brand-blue dark:text-dark-brand-blue' : ''
                ]">{{ $t("Due date") }}</label>
              <div class="rounded-md shadow-sm">
                <DatePicker
                  ref="due_date"
                  v-model="due_date"
                  :is-dark="$store.state.ui.darkMode"
                  class="bg-white dark:bg-dark-screen-background"
                  is-inline
                  mode="date"
                  :popover="{ visibility: 'none' }">
                  <template #default="{ inputValue, inputEvents }">
                    <div class="relative">
                      <svg
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        class="absolute w-4 h-full mx-2 text-gray-600 pointer-events-none">
                        <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>

                      <input
                        id="create_plan_d_date"
                        :value="inputValue ? inputValue : relative_date_text"
                        autocomplete="off"
                        class="w-full h-10 px-10 pl-8 pr-2 transition ease-in-out border rounded-lg outline-none text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue duration-side-menu-transition-duration"
                        :class="[
                          !isBoxBackground
                            ? 'bg-light-box-background dark:bg-dark-box-background'
                            : 'bg-light-screen-background dark:bg-dark-box-background'
                        ]"
                        @focusin="focusDueDateInput"
                        @focusout="focusDueDateInput"
                        @click="is_modal_relative_date_open = true"
                        v-on="inputEvents"
                        @input="
                          $utils.debounce(() => {
                            due_date = $event.target?.value
                          }, 800)
                        ">
                    </div>
                    <div v-if="is_modal_relative_date_open" class="absolute z-20">
                      <DateTimeFrame
                        class="w-full mr-2 bg-white rounded-lg dark:bg-dark-box-background"
                        @done="
                          ($event) => {
                            due_date = $event
                            is_modal_relative_date_open = false
                          }
                        "
                        @relative-date-changed="
                          ($event) => {
                            relative_date = $event
                            is_modal_relative_date_open = false
                          }
                        "
                        @cancel="is_modal_relative_date_open = false" />
                    </div>
                  </template>

                  <template #footer>
                    <div class="flex items-center justify-end pr-2 w-full pb-2 mt-0">
                      <button v-if="due_date || relative_date" type="button" class="cancel-btn inline-block" @click.stop="emptyDueDate">
                        {{ $t("Clear") }}
                      </button>
                    </div>
                  </template>
                </DatePicker>
              </div>
            </div>
          </div>

          <div :class="[{ '!mt-6': inPlaybook }]" class="flex items-center mt-0 mr-6 gap-x-2">
            <Switch
              v-model="auto_set_start_date"
              :class="[
                auto_set_start_date ? 'bg-blue' : 'bg-gray-200',
                'relative inline-flex flex-shrink-0 h-7 w-12 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
              ]">
              <span
                aria-hidden="true"
                :class="[
                  auto_set_start_date && $store.state.portfolio.ui_options?.language == 'ar'
                    ? '-translate-x-5'
                    : auto_set_start_date
                      ? 'translate-x-5'
                      : 'translate-x-0',
                  'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                ]" />
            </Switch>
            <span class="dark:text-common-grey">{{ $t("Auto Set Start Date") }}</span>
          </div>

          <div>
            <div class="w-[800px] mt-3 mb-5">
              <div v-if="isAllRecords" class="mt-2 whitespace-nowrap text-common-grey">
                {{ count - excludedRecords.length }} {{ $t("out of") }} {{ count }} {{ $t("records in table selected") }}
              </div>

              <CompaniesSelect
                v-else
                playbook-object-type="company"
                :short-king="inPlaybook"
                :label="$t('Companies')"
                :companies="selected_companies"
                :autocomplete-options="resultReferencesList"
                @changed="
                  ($event) => {
                    handleSetConmpaniesList($event)
                  }
                " />
            </div>
          </div>

          <div class="flex w-full">
            <div class="w-1/2 mt-3 mb-5">
              <TeamMembersSelect
                v-model:selected-team-members="selected_team_members"
                :autocomplete-options="autocompleteOptionsInPlaybook"
                :label="$t('Team Members')" />
            </div>
            <div v-if="!inPlaybook" class="w-1/2 mt-3 mb-5">
              <ContactsSelect v-model:selected-contacts="selected_contacts" :label="$t('Contacts')" :companies="selected_companies" />
            </div>
          </div>

          <div class="cursor-pointer" @click="assign_companies_to_goals_and_tasks = !assign_companies_to_goals_and_tasks">
            <span class="mt-1 mr-2 text-common-grey">{{ $t("Assign selected companies to tasks and goals") }} </span>
            <input
              :disabled="true"
              :checked="assign_companies_to_goals_and_tasks"
              type="checkbox"
              class="form-checkbox pointer-events-none text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-400 w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration">
          </div>

          <div class="cursor-pointer" @click="assign_collaborators_to_goals_and_tasks = !assign_collaborators_to_goals_and_tasks">
            <span class="mt-1 mr-2 text-common-grey">{{ $t("Assign selected team members to tasks and goals") }} </span>
            <input
              :disabled="true"
              :checked="assign_collaborators_to_goals_and_tasks"
              type="checkbox"
              class="form-checkbox pointer-events-none text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-400 w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration">
          </div>

          <div class="flex items-center gap-x-2">
            <label class="flex items-baseline justify-between font-medium text-gray-500 text-md">{{ $t("Assigned goals") }}:</label>

            <div
              v-if="!isEditTemplate && !inPlaybook"
              class="btn ml-auto w-fit primary-btn"
              @click="!isEditTemplate && !inPlaybook ? handleCreateGoal() : null">
              {{ $t("Create New Goal") }}
            </div>

            <Menu v-else
                  v-slot="{ close }"
                  ref="goalsMenu"
                  as="div"
                  class="relative flex-shrink-0 ml-auto">
              <MenuButton
                class="inline-flex items-center justify-center rounded-full w-36 border-1 focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue">
                <div class="btn primary-btn">
                  {{ $t("Add Goal to Plan") }}
                </div>
              </MenuButton>
              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0">
                <MenuItems class="absolute z-50 w-40 mx-3 mt-3 -ml-48 origin-bottom-right bg-transparent border-0 focus:outline-none">
                  <div class="py-1 bg-white dark:bg-dark-screen-background p-3 border dark:border-dark-divider-border rounded-lg w-[340px]">
                    <div>
                      <div v-if="selected_template" class="flex w-full justify-end">
                        <div
                          :class="{ 'text-white bg-light-brand-blue': list_to_get == 'goals' }"
                          class="p-1 px-3 rounded-full border cursor-pointer"
                          @click="list_to_get = 'goals'">
                          {{ $t("Goals") }}
                        </div>
                        <div
                          :class="{ 'text-white bg-light-brand-blue': list_to_get == 'templates' }"
                          class="p-1 px-3 rounded-full border ml-2 cursor-pointer"
                          @click="list_to_get = 'templates'">
                          {{ $t("Templates") }}
                        </div>
                      </div>

                      <AsyncAutoCompleteInput
                        v-if="isEditTemplate || inPlaybook"
                        :add="false"
                        :add-label="$t('Create Goal')"
                        :selected-items="selected_goals"
                        :clear-on-select="true"
                        :is-box-background="true"
                        classes="w-full"
                        input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm "
                        name="goal"
                        value-attr="id"
                        :endpoint="
                          list_to_get == 'goals'
                            ? `/api/organizations/${$store.state.organization.id}/goals/`
                            : `/api/organizations/${$store.state.organization.id}/templates?source_object_type=goal`
                        "
                        type="text"
                        :placeholder="$t('Search for goal to add')"
                        :debounce="500"
                        @chosen="
                          ($event) => {
                            handleGoalSelected($event, close)
                          }
                        " />
                    </div>
                    <div
                      v-if="!inPlaybook"
                      class="mt-3 mb-3 btn primary-btn"
                      @click="
                        () => {
                          close()
                          handleCreateGoal()
                        }
                      ">
                      + {{ $t("Create New Goal") }}
                    </div>
                  </div>
                </MenuItems>
              </transition>
            </Menu>
          </div>
          <div
            v-if="!goalsCurrentPageItems.length"
            class="relative w-full p-4 text-center border rounded-lg top-4 dark:border-dark-divider-border text-common-grey">
            <div class="text-lg">
              {{ $t("No goals to display") }}
            </div>
            <div class="mt-1 text-sm">
              {{ $t("Add new goals to plan") }}
            </div>
          </div>

          <draggable
            v-model="goalsCurrentPageItems"
            as="div"
            static
            :class="[
              'bg-white dark:border-dark-divider-border border-white dark:bg-dark-box-background py-4 px-2',
              `grid ${fullScreen ? 'grid-cols-2' : 'grid-cols-1'} gap-x-8 overflow-visible mt-1 border-0 ${
                $store.state.ui.darkMode ? 'dark-scrollbar' : ''
              } dark:border-dark-divider-border rounded-lg text-base ring-0 overflow-auto focus:outline-none sm:text-sm transition ease-in-out duration-side-menu-transition-duration`
            ]"
            :component-data="{ type: 'transition-group' }"
            v-bind="{
              disabled: goalsCurrentPageItems.length < selected_goals.length || goalsCurrentPageItems < 2,
              animation: 200,
              group: 'group',
              ghostClass: 'ghost',
              chosenClass: 'chosen'
            }"
            item-key="id">
            <template #item="{ element: goal }">
              <div class="bg-white cursor-pointer">
                <div>
                  <div
                    v-if="expanded_goals?.includes(goal.id)"
                    class="relative p-4 mb-4 border rounded-md dark:bg-dark-screen-background dark:border-dark-divider-border dark:text-white"
                    @mouseenter="handleGoalEnter(goal)"
                    @mouseleave="handleGoalLeave">
                    <div class="flex items-center">
                      <ToggleStatusButton
                        :prevent-request="isEditTemplate"
                        :object="{ ...goal, otype: 'goal' }"
                        @statusChanged="
                          ($event) => {
                            handleStatusChanged($event, goal)
                          }
                        " />

                      <div class="flex ml-2">
                        <div>{{ goal.name }}</div>
                        <ClipboardCheck class="absolute opacity-0 pointer-events-none" />
                        <Badge :icon="task_icon" :object="goal" :list-name="'tasks'" />
                      </div>
                      <div class="absolute flex -right-3 -top-3 gap-x-2">
                        <div
                          class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                          @click="handleExpandGoal(goal)">
                          <div class="w-[15px] h-[15px] ml-[5px] mt-[0px] border-b-[3px] border-b-[#45D72D]" />
                          <span
                            class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                            {{ $t("Minimize") }}</span>
                        </div>
                        <div
                          class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:bg-dark-screen-background dark:border-dark-divider-border"
                          @click="handleViewGoal(goal)">
                          <Maximize2
                            class="w-5 h-5 ml-[3px] mt-[3px] transition ease-in-out duration-side-menu-transition-duration"
                            color="#2DA7D7" />
                          <span
                            class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm w-[80px] bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                            {{ $t("View Goal") }}</span>
                        </div>
                        <div
                          class="flex items-center justify-center bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                          @click="handleRemoveGoal(goal)">
                          <X color="#FF0000" class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" /><span
                            class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                            {{ $t("Remove") }}</span>
                        </div>
                      </div>
                    </div>
                    <div class="flex w-full mt-5">
                      <div v-if="goal.id" class="mt-2 w-[40%]">
                        <!-- <div  class="flex">
                          <div class="flex gap-x-2">
                            <div v-if="goal?.filter" class="text-common-grey">
                              Progress:
                            </div>
                            <div v-else class="text-sm text-common-grey">
                              No filter specified for this goal
                            </div>
                            <div v-if="goal?.filter" class="text-sm font-light text-md dark:text-white">
                              {{

                                goal?.record?.values[goal.filter.split(" ")[1]]
                                  ? $utils.truncateNumber(parseInt(goal?.record?.values[goal.filter.split(" ")[1]]))
                                  : 0
                              }}/{{ $utils.truncateNumber(parseInt(goal.filter.split(" ")[goal.filter.split(" ")?.length - 2])) }}
                              {{ $utils.getMetricByReferenceName(goal.filter.split(" ")[1])?.name }}
                            </div>
                          </div>
                          <div v-if="goal?.record?.values[goal.filter?.split(' ')[1]]" class="ml-auto text-lg">
                            {{
                              Math.round(
                                (parseInt(goal?.record.values[goal.filter.split(" ")[1]]) /
                                  parseInt(goal.filter.split(" ")[goal.filter.split(" ")?.length - 2])) *
                                  100
                              )
                            }}%
                          </div>
                        </div> -->

                        <!-- <div v-if="goal.filter" class="py-4 -mt-3 text-sm text-gray-500 whitespace-nowrap">
                          <div class="w-full h-2 bg-gray-300 rounded-full dark:bg-dark-box-background">
                            <div
                              :style="{
                                width: `${

                                  (goal?.record?.values[goal.filter.split(' ')[1]] ? goal?.record?.values[goal.filter.split(' ')[1]] : 0) /
                                  goal.filter.split(' ')[goal.filter.split(' ')?.length - 2] >
                                  1
                                    ? 100
                                    : `${
                                      (goal?.record?.values[goal.filter.split(' ')[1]]
                                        ? goal?.record?.values[goal.filter.split(' ')[1]]
                                        : 0) / goal.filter.split(' ')[goal.filter.split(' ')?.length - 2]
                                    }` * 100
                                }%`
                              }"
                              :class="[` rounded-full bg-blue h-2`]" />
                          </div>
                        </div> -->
                      </div>
                      <div class="ml-20 w-[10%]">
                        <div class="flex flex-row items-center gap-2">
                          <div class="text-common-grey whitespace-nowrap">
                            {{ $t("Due Date") }}:
                          </div>

                          <div class="flex items-start justify-start w-1/12 py-2 text-start whitespace-nowrap text-md">
                            <span v-if="goal.due_date">
                              {{ $moment(goal.due_date).format("DD MMM  YYYY") }}
                            </span>

                            <span v-else> - </span>
                          </div>
                        </div>
                      </div>
                      <div :class="[`${goal.assigned_to?.length > 4 ? 'w-[16%]' : ''}`]" class="ml-auto -mt-8">
                        <div
                          :class="[`${goal.assigned_to?.length > 4 ? '' : '-ml-10'}`]"
                          class="mb-2 text-common-grey whitespace-nowrap text-start">
                          {{ $t("Team Members") }}
                        </div>
                        <div class="flex items-center -ml-5 -space-x-4 overflow-hidden">
                          <span v-for="user in goal.assigned_to?.slice(0, 4)" :key="user.id" class="inline-block">
                            <Avatar
                              :first-name="user.first_name"
                              :last-name="user.last_name"
                              :image="user.avatar"
                              :tooltip="`${Record.from(user).full_name} <${Record.from(user).email}>`" />
                          </span>
                          <span
                            v-if="goal.assigned_to?.length > 4"
                            class="relative ml-auto text-sm left-6 text-light-brand-blue dark:text-dark-brand-blue">
                            + {{ goal.assigned_to?.length - 4 }} {{ $t("more") }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-else
                    class="relative flex p-4 mb-4 align-middle border rounded-md dark:bg-dark-screen-background dark:border-dark-divider-border dark:text-white"
                    @mouseenter="handleGoalEnter(goal)"
                    @mouseleave="handleGoalLeave">
                    <div
                      :class="[
                        'w-4 h-4 rounded-full mt-1 mr-4 relative',
                        {
                          'bg-status-warning-yellow': getActualStatus(goal, goal.status) == $t('overdue'),
                          'bg-dark-brand-blue': ['in-progress', 'pending'].includes(goal.status),

                          'bg-status-success-green': goal.status == 'completed',
                          'bg-status-error-red': ['cancelled', 'missed'].includes(goal.status)
                        }
                      ]" />

                    <div class="flex ml-2">
                      <Tally2
                        v-if="goalsCurrentPageItems.length == selected_goals.length && goalsCurrentPageItems.length > 1"
                        class="h-5 w-5 mr-2 text-common-grey rotate-90"
                        aria-hidden="true" />
                      <div>{{ goal.name }}</div>
                      <Badge :icon="task_icon" :object="goal" :list-name="$t('tasks')" />
                    </div>
                    <div v-if="goal.id" class="ml-auto mr-20">
                      <!-- <div v-if="goal?.record?.values[goal.filter?.split(' ')[1]]" class="ml-auto text-md text-common-grey">
                        {{
                          Math.round(
                            (parseInt(goal?.record.values[goal.filter.split(" ")[1]]) /
                              parseInt(goal.filter.split(" ")[goal.filter.split(" ").length - 2])) *
                              100
                          )
                        }}%
                      </div> -->
                    </div>
                    <div class="absolute flex -right-3 -top-3 gap-x-2">
                      <div
                        class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                        @click="handleExpandGoal(goal)">
                        <div class="w-[15px] h-[15px] ml-[5px] mt-[5px] border-[3px] rounded-md border-[#45D72D]" />
                        <span
                          class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                          {{ $t("Expand") }}</span>
                      </div>
                      <div
                        class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:bg-dark-screen-background dark:border-dark-divider-border"
                        @click="handleViewGoal(goal)">
                        <Maximize2
                          class="w-5 h-5 ml-[3px] mt-[3px] transition ease-in-out duration-side-menu-transition-duration"
                          color="#2DA7D7" />
                        <span
                          class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm w-[80px] bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                          {{ $t("View Goal") }}</span>
                      </div>
                      <div
                        class="flex items-center justify-center bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                        @click="handleRemoveGoal(goal)">
                        <X color="#FF0000" class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" /><span
                          class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                          {{ $t("Remove") }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </draggable>

          <div class="flex items-center w-full !-mt-4">
            <div
              v-if="selected_goals.length > 6 && selected_goals.length != goalsPerPage"
              class="mr-2 text-sm text-blue cursor-pointer"
              @click="goalsPerPage = selected_goals.length">
              {{ $t("Show all") }} ({{ selected_goals.length }})
            </div>
            <div v-else-if="selected_goals.length > 6" class="mr-2 text-sm text-blue cursor-pointer" @click="goalsPerPage = 6">
              {{ $t("Show less") }}
            </div>

            <Pagination
              v-if="selected_goals.length > goalsPerPage"
              class="ml-auto"
              :total-pages="goalsTotalPages"
              :current-page="goalsCurrentPage"
              @input="goalsChangePage" />
          </div>

          <div class="flex items-center gap-x-2">
            <label class="flex items-baseline justify-between font-medium text-gray-500 text-md">{{ $t("Assigned tasks") }}:</label>

            <div
              v-if="!isEditTemplate && !inPlaybook"
              class="btn ml-auto w-fit primary-btn"
              @click="!isEditTemplate && !inPlaybook ? handleCreateTask() : null">
              {{ $t("Create New Task") }}
            </div>

            <Menu v-else
                  v-slot="{ close }"
                  ref="goalsMenu"
                  as="div"
                  class="relative flex-shrink-0 ml-auto">
              <MenuButton
                class="inline-flex items-center justify-center rounded-full w-36 border-1 focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue">
                <div class="btn primary-btn">
                  {{ $t("Add Task to Plan") }}
                </div>
              </MenuButton>
              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0">
                <MenuItems class="absolute z-50 w-40 mx-3 mt-3 -ml-48 origin-bottom-right bg-transparent border-0 focus:outline-none">
                  <div class="py-1 bg-white p-3 border dark:border-dark-divider-border dark:bg-dark-screen-background rounded-lg w-[340px]">
                    <div>
                      <div v-if="selected_template" class="flex w-full justify-end">
                        <div
                          :class="{ 'text-white bg-light-brand-blue': list_to_get_task == 'tasks' }"
                          class="p-1 px-3 rounded-full border cursor-pointer"
                          @click="list_to_get_task = 'tasks'">
                          {{ $t("Tasks") }}
                        </div>
                        <div
                          :class="{ 'text-white bg-light-brand-blue': list_to_get_task == 'templates' }"
                          class="p-1 px-3 rounded-full border ml-2 cursor-pointer"
                          @click="list_to_get_task = 'templates'">
                          {{ $t("Templates") }}
                        </div>
                      </div>

                      <AsyncAutoCompleteInput
                        v-if="isEditTemplate || inPlaybook"
                        :add="false"
                        :add-label="$t('Create Task')"
                        :selected-items="selected_tasks"
                        :clear-on-select="true"
                        :is-box-background="true"
                        classes="w-full"
                        input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm "
                        name="task"
                        value-attr="id"
                        :endpoint="
                          list_to_get_task == 'tasks'
                            ? `/api/organizations/${$store.state.organization.id}/tasks/`
                            : `/api/organizations/${$store.state.organization.id}/templates?source_object_type=task`
                        "
                        type="text"
                        :placeholder="$t('Search for task to add')"
                        :debounce="500"
                        @chosen="
                          ($event) => {
                            handleTaskSelected($event, close)
                          }
                        " />
                    </div>
                    <div
                      v-if="!inPlaybook"
                      class="mt-3 mb-3 btn primary-btn"
                      @click="
                        () => {
                          close()
                          handleCreateTask()
                        }
                      ">
                      + {{ $t("Create New Task") }}
                    </div>
                  </div>
                </MenuItems>
              </transition>
            </Menu>
          </div>

          <div
            v-if="!tasksCurrentPageItems.length"
            class="relative w-full p-4 text-center border rounded-lg top-4 dark:border-dark-divider-border text-common-grey">
            <div class="text-lg">
              {{ $t("No tasks to display") }}
            </div>
            <div class="mt-1 text-sm">
              {{ $t("Add new tasks to plan") }}
            </div>
          </div>

          <draggable
            v-model="displayed_tasks"
            as="div"
            static
            :class="[
              'bg-white dark:border-dark-divider-border border-white dark:bg-dark-box-background py-4 px-2',
              `grid ${fullScreen ? 'grid-cols-2' : 'grid-cols-1'} gap-x-8 overflow-visible mt-1 border-0 ${
                $store.state.ui.darkMode ? 'dark-scrollbar' : ''
              } dark:border-dark-divider-border rounded-lg text-base ring-0 overflow-auto focus:outline-none sm:text-sm transition ease-in-out duration-side-menu-transition-duration`
            ]"
            :component-data="{ type: 'transition-group' }"
            v-bind="{
              disabled: tasksCurrentPageItems.length < selected_tasks.length || tasksCurrentPageItems < 2,
              animation: 200,
              group: 'group',
              ghostClass: 'ghost',
              chosenClass: 'chosen'
            }"
            item-key="id"
            @end="endTaskDrag()">
            <template #item="{ element: task }">
              <div class="bg-white cursor-pointer">
                <div
                  v-if="expanded_tasks.includes(task.id)"
                  class="relative p-4 mb-4 border rounded-md dark:bg-dark-screen-background dark:border-dark-divider-border dark:text-white"
                  @mouseenter="handleTaskEnter(task)"
                  @mouseleave="handleTaskLeave">
                  <div class="flex items-center">
                    <ToggleStatusButton
                      :prevent-request="isEditTemplate"
                      :object="{ ...task, otype: 'task' }"
                      @statusChanged="
                        ($event) => {
                          handleStatusChanged($event, task)
                        }
                      " />
                    <div class="ml-2">
                      {{ task.name }}
                    </div>
                    <div class="absolute flex -right-3 -top-3 gap-x-2">
                      <div
                        class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                        @click="handleExpandTask(task)">
                        <div class="w-[15px] h-[15px] ml-[5px] mt-[0px] border-b-[3px] border-b-[#45D72D]" />
                        <span
                          class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                          {{ $t("Minimize") }}</span>
                      </div>
                      <div
                        class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:bg-dark-screen-background dark:border-dark-divider-border"
                        @click="handleViewTask(task)">
                        <Maximize2
                          class="w-5 h-5 ml-[3px] mt-[3px] transition ease-in-out duration-side-menu-transition-duration"
                          color="#2DA7D7" />
                        <span
                          class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm w-[80px] bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                          {{ $t("View Task") }}</span>
                      </div>
                      <div
                        class="flex items-center justify-center bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                        @click="handleRemoveTask(task)">
                        <X color="#FF0000" class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" /><span
                          class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                          {{ $t("Remove") }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="flex w-full mt-5">
                    <div class="w-1/3">
                      <div class="flex flex-row items-center gap-2">
                        <div class="text-common-grey">
                          {{ $t("Priority") }}:
                        </div>

                        <div class="flex items-start justify-start w-1/12 py-2 text-start whitespace-nowrap text-md">
                          <div v-if="task.priority" class="flex items-center capitalize gap-x-2">
                            <div>
                              {{ task.priority }}
                            </div>
                            <div>
                              <div v-if="task.priority == $t('normal')">
                                <div :class="[`border-b-2 w-3  border-status-warning-yellow`]" />
                                <div :class="[`border-b-2 w-3 mt-1 border-status-warning-yellow`]" />
                              </div>
                              <div v-else-if="task.priority == $t('high')">
                                <ChevronRight class="w-5 h-5 -rotate-90 text-status-error-red" />
                              </div>
                              <div v-else-if="task.priority == $t('highest')">
                                <ChevronsRight class="w-5 h-5 -rotate-90 text-status-error-red" />
                              </div>
                              <div v-else-if="task.priority == $t('low')">
                                <ChevronRight class="w-5 h-5 rotate-90 text-status-success-green" />
                              </div>
                              <div v-else-if="task.priority == $t('lowest')">
                                <ChevronsRight class="w-5 h-5 rotate-90 text-status-success-green" />
                              </div>
                            </div>
                          </div>

                          <span v-else> - </span>
                        </div>
                      </div>
                    </div>

                    <div class="w-1/3">
                      <div class="flex flex-row items-center gap-2">
                        <div class="text-common-grey whitespace-nowrap">
                          {{ $t("Due Date") }}:
                        </div>

                        <div class="flex items-start justify-start w-1/12 py-2 text-start whitespace-nowrap text-md">
                          <span v-if="task.due_date">
                            {{ $moment(task.due_date).format("DD MMM  YYYY") }}
                          </span>

                          <span v-else> - </span>
                        </div>
                      </div>
                    </div>

                    <div :class="[`${task.assigned_to.length > 4 ? 'w-[16%]' : ''}`]" class="ml-auto -mt-8">
                      <div
                        :class="[`${task.assigned_to.length > 4 ? '' : '-ml-10'}`]"
                        class="mb-2 text-common-grey whitespace-nowrap text-start">
                        {{ $t("Team Members") }}
                      </div>
                      <div class="flex items-center -ml-5 -space-x-4 overflow-hidden">
                        <span v-for="user in task.assigned_to?.slice(0, 4)" :key="user" class="inline-block has-tooltip">
                          <img
                            v-if="$utils.getUserById(user, 'avatar')"
                            class="inline-block rounded-full h-9 w-9 object-cover"
                            :src="$utils.getUserById(user, 'avatar')"
                            alt="">
                          <Avatar
                            v-else
                            :first-name="$utils.getUserById(user, 'first_name')"
                            :last-name="$utils.getUserById(user, 'last_name')" />
                          <span class="p-1 bg-gray-300 rounded-sm tooltip whitespace-nowrap">
                            <div class="">
                              <p class="text-sm font-medium dark:!text-white text-gray-700 group-hover:text-gray-900">
                                {{ $utils.getUserById(user, "full_name") }}
                              </p>
                            </div>
                          </span>
                        </span>
                        <span
                          v-if="task.assigned_to.length > 4"
                          class="relative ml-auto text-sm left-6 text-light-brand-blue dark:text-dark-brand-blue">
                          + {{ task.assigned_to.length - 4 }} {{ $t("more") }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  v-else
                  class="relative flex p-4 mb-4 align-middle border rounded-md dark:bg-dark-screen-background dark:border-dark-divider-border dark:text-white"
                  @mouseenter="handleTaskEnter(task)"
                  @mouseleave="handleTaskLeave">
                  <div
                    :class="[
                      'w-4 h-4 rounded-full mt-1 mr-4 relative',
                      {
                        'bg-status-warning-yellow': getActualStatus(task, task.status) == $t('overdue'),
                        'bg-dark-brand-blue': ['in-progress', 'pending'].includes(task.status),

                        'bg-status-success-green': task.status == 'completed',
                        'bg-status-error-red': ['cancelled', 'missed'].includes(task.status)
                      }
                    ]" />

                  <Tally2
                    v-if="tasksCurrentPageItems.length == selected_tasks.length && tasksCurrentPageItems.length > 1"
                    class="h-5 w-5 mr-2 text-common-grey rotate-90"
                    aria-hidden="true" />

                  <div>{{ task.name }}</div>

                  <div class="absolute flex -right-3 -top-3 gap-x-2">
                    <div
                      class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                      @click="handleExpandTask(task)">
                      <div class="w-[15px] h-[15px] ml-[5px] mt-[5px] border-[3px] rounded-md border-[#45D72D]" />
                      <span
                        class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                        {{ $t("Expand") }}</span>
                    </div>
                    <div
                      class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:bg-dark-screen-background dark:border-dark-divider-border"
                      @click="handleViewTask(task)">
                      <Maximize2
                        class="w-5 h-5 ml-[3px] mt-[3px] transition ease-in-out duration-side-menu-transition-duration"
                        color="#2DA7D7" />
                      <span
                        class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm w-[80px] bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                        {{ $t("View Task") }}</span>
                    </div>
                    <div
                      class="flex items-center justify-center bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                      @click="handleRemoveTask(task)">
                      <X color="#FF0000" class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" /><span
                        class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                        {{ $t("Remove") }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </draggable>

          <div class="flex items-center w-full !-mt-4">
            <div
              v-if="selected_tasks.length > 6 && selected_tasks.length != tasksPerPage"
              class="mr-2 text-sm text-blue cursor-pointer"
              @click="tasksPerPage = selected_tasks.length">
              {{ $t("Show all") }} ({{ selected_tasks.length }})
            </div>
            <div v-else-if="selected_tasks.length > 6" class="mr-2 text-sm text-blue cursor-pointer" @click="tasksPerPage = 6">
              {{ $t("Show less") }}
            </div>
            <Pagination
              v-if="selected_tasks.length > tasksPerPage"
              class="ml-auto"
              :total-pages="tasksTotalPages"
              :current-page="tasksCurrentPage"
              @input="tasksChangePage" />
          </div>

          <div class="relative rounded-md shadow-sm border p-2 mt-2">
            <div class="flex items-center justify-between w-full">
              <label class="block text-common-grey">{{ $t("Tags") }}</label>
              <button class="primary-btn w-fit" @click.prevent="is_tags_modal_open = true">
                <Plus class="w-6 h-6 mr-2" aria-hidden="true" />
                <span>{{ selected_tags.length ? $t("Edit Tags") : $t("Add Tags") }}</span>
              </button>
            </div>
            <div v-if="selected_tags.length" class="flex flex-wrap gap-2 mt-4">
              <div
                v-for="tag in selected_tags"
                :key="tag?.id"
                class="flex focus:outline-none rounded-md"
                :style="{ background: tag.color }">
                <div v-if="selected_tags.find((t) => t.id == tag.id)" class="flex items-center group">
                  <span
                    class="flex items-center rounded-md px-2 py-1.5 border border-gray-300"
                    :style="{
                      background: tag.color
                    }">
                    <span class="block text-white truncate">{{ tag.name }}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="is_adding_note || is_editing_note" class="px-2 pb-3 mt-5 border rounded-md shadow-sm dark:border-dark-divider-border">
            <div class="my-2 font-semibold border dark:border-dark-divider-border">
              <TextInput
                :no-border="true"
                name="note_title"
                :value="note_title"
                :is-box-background="true"
                type="text"
                :placeholder="$t('Title')"
                input-classes="border border-red-500 appearance-none block w-full px-3 py-2 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                @input="
                  $utils.debounce(() => {
                    note_title = $event.target.value
                  }, 800)
                " />
            </div>
            <div class="mt-0">
              <div class="h-24 mt-0 border-0">
                <TextEditor
                  :value="note_text"
                  :show-label="false"
                  :no-border="true"
                  toolbar="essential"
                  :placeholder="$t('Note description')"
                  name="note_text"
                  :label="$t('Description')"
                  theme="snow"
                  input-classes="rounded-b-md"
                  @changed="
                    (e) => {
                      handleEditNoteText(e)
                    }
                  " />
              </div>
            </div>
            <div class="flex flex-row justify-end w-full gap-2 pt-12">
              <span class="w-1/2" />
              <button
                type="button"
                class="w-1/4 m-0 cancel-btn"
                @click="
                  () => {
                    is_adding_note = false
                    is_editing_note = false
                  }
                ">
                {{ $t("Cancel") }}
              </button>
              <button type="button" class="w-1/4 primary-btn" @click="saveNote">{{ $t("Save") }}</button>
            </div>
          </div>

          <div
            v-else
            class="flex items-center content-center w-full p-2 mt-5 font-normal border border-solid rounded cursor-pointer text-common-grey dark:border-dark-divider-border"
            @click="handleAddNote">
            <NotebookPen
              :color="$store.state.ui.darkMode ? '#fff' : '#899399'"
              class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration"
              @click="is_adding_note = true" />

            <div class="pl-1 ml-3">
              {{ $t("Write a new note") }}
            </div>
          </div>
          <div class="mt-2 text-sm text-red-500">
            {{ notes_error_msg }}
          </div>

          <div
            v-if="!is_editing_note"
            class="grid grid-cols-3 gap-3 ease-in-out rounded-lg shadow-none dark:bg-dark-screen-backgroundtransition duration-side-menu-transition-duration">
            <div v-for="(note, index) in notesCurrentPageItems" :key="note">
              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0">
                <div class="relative" @mouseenter="note_hover_index = index" @mouseleave="note_hover_index = null">
                  <div v-if="index == note_hover_index" class="absolute flex cursor-pointer right-2 gap-x-2 top-2">
                    <Pencil
                      :color="$store.state.ui.darkMode ? '#fff' : '#899399'"
                      class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration"
                      @click="editNote(note)" />
                    <Trash2
                      :color="$store.state.ui.darkMode ? '#fff' : '#899399'"
                      class="w-5 h-5 transition ease-in-out dark:text-white duration-side-menu-transition-duration"
                      @click="deleteNote(note)" />
                  </div>
                  <div class="p-3 bg-white border rounded-lg dark:border-dark-divider-border dark:bg-dark-box-background">
                    <div class="text-lg font-semibold dark:text-white">
                      {{ note.title }}
                    </div>
                    <TextEditor
                      :disabled="true"
                      :no-border="true"
                      :value="note.description"
                      toolbar="essential"
                      name="note_description"
                      label=""
                      theme="bubble" />
                  </div>
                </div>
              </transition>
            </div>
          </div>
          <div class="flex items-center w-full">
            <div v-if="notes?.length > 3" class="mr-2 text-sm text-common-grey">
              {{ $t("Show") }}
            </div>
            <SelectInput
              v-if="notes?.length > 3"
              :value="notesPerPage"
              :is-box-background="true"
              class="w-[115px]"
              :input-classes="'text-left'"
              name="page_show_options"
              :allow-null="false"
              :items="notes_page_show_options"
              @update:currentItem="notesPerPage = $event" />
            <div v-if="notes?.length > 3" class="ml-2 text-sm text-common-grey">
              {{ $t("in page") }}
            </div>
            <Pagination
              v-if="notes?.length > notesPerPage"
              class="ml-auto"
              :total-pages="notesTotalPages"
              :current-page="notesCurrentPage"
              @input="notesChangePage" />
          </div>

          <AttachmentsField v-model="attachments" open-position="left" />

          <div class="flex flex-col items-end w-full pt-2 text-status-error-red">
            <p v-for="error in errors" :key="error">
              {{ error }}
            </p>
          </div>

          <div class="flex flex-row justify-between w-full gap-2 pt-6">
            <button type="button" class="w-1/2 m-0 cancel-btn" @click="handleClosePlan">{{ $t("Cancel") }}</button>
            <button v-if="!is_edit_template" :disabled="submitting_is_on" type="submit" class="w-1/2 primary-btn">
              <RotateCw
                :class="{
                  'animate-spin': submitting_is_on,
                  'opacity-100': submitting_is_on
                }"
                class="w-5 h-5 mr-3 -ml-1 opacity-0"
                aria-hidden="true" />

              {{ inPlaybook ? $t("Save") : $t("Create") }} {{ $t("Plan") }}
            </button>
            <button v-else type="submit" class="w-1/2 primary-btn">{{ $t("Update template") }}</button>
          </div>
        </component>
      </div>
    </div>

    <!-- Delete Note -->
    <Modal v-model:is-open="is_delete_note_modal_open" max-width="max-w-[600px] mx-5" :default-success-msg="$t('Note Deleted')">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Delete Note") }} {{ note_to_be_deleted.title }}
        </h3>
        <div class="max-w-xl mt-2 text-sm text-gray-500">
          <p>{{ $t("Once you delete a note, you can't restore it.") }}</p>
        </div>
        <div class="mt-5 text-center">
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="confirmDeleteNote(showIsDone)">
            {{ $t("Delete note") }}
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 ml-2 font-medium border rounded-md dark:text-dark-body-text focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm"
            @click="closeModal">
            {{ $t("Cancel") }}
          </button>
        </div>
      </template>
      <template #done="{ close: closeModal }">
        <div>
          <div class="flex items-center justify-center w-12 h-12 mx-auto bg-green-100 rounded-full">
            <Check class="w-6 h-6 text-green-600" aria-hidden="true" />
          </div>
          <div class="mt-3 text-center sm:mt-5">
            <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
              {{ $t("Note deleted successfully") }}
            </h3>
          </div>
        </div>
        <div class="float-right mt-5 sm:mt-6">
          <button type="button" class="cancel-btn" @click="closeModal">{{ $t("Done") }}</button>
        </div>
      </template>
    </Modal>

    <!-- Add tags -->
    <Modal v-model:is-open="is_tags_modal_open" :default-success-msg="$t('Note Deleted')">
      <template #form="{ close: closeModal }">
        <TagsAutoCompleteInput
          :placeholder="$t('Add new tag')"
          value-attr="full_name"
          :selected-tags="selected_tags"
          @changed="
            ($event) => {
              handleTagsChanged($event)
            }
          "
          @close="closeModal" />
      </template>
    </Modal>
  </div>
</template>

<script>
import * as Yup from "yup"
import Modal from "@/components/shared/Modal.vue"
import { DatePicker } from "v-calendar"
import TextInput from "@/components/shared/TextInput.vue"
import SelectInput from "@/components/shared/SelectInput.vue"
import TextEditor from "@/components/shared/TextEditor.vue"
import TagsAutoCompleteInput from "@/components/shared/TagsAutoCompleteInput.vue"
import dataService from "@/services/data.service"
import { event as google_event } from "vue-gtag-next"
import ContactsSelect from "@/components/shared/ContactsSelect.vue"
import CompaniesSelect from "@/components/shared/CompaniesSelect.vue"
import TeamMembersSelect from "@/components/shared/TeamMembersSelect.vue"
import {
  NotebookPen,
  Maximize2,
  ClipboardCheck,
  X,
  ChevronsRight,
  ChevronRight,
  Tally2,
  Check,
  Plus,
  Trash2,
  RotateCw,
  Pencil
} from "lucide-vue-next"
import { Form } from "vee-validate"
import AsyncAutoCompleteInput from "@/components/shared/AsyncAutoCompleteInput.vue"
import Pagination from "@/components/shared/Pagination.vue"
import DateTimeFrame from "@/components/shared/DateTimeFrame.vue"
import { Menu, MenuButton, MenuItems } from "@headlessui/vue"
import AttachmentsField from "@/components/shared/form-inputs/AttachmentsField.vue"
import ToggleStatusButton from "../shared/ToggleStatusButton.vue"
import Avatar from "@/components/shared/Avatar.vue"
import { Switch } from "@headlessui/vue"
import { useStore } from "vuex"
import { Record } from "@/models/record"
import SelectTemplateDropDownMenu from "@/components/shared/SelectTemplateDropDownMenu.vue"
import Badge from "@/components/shared/ActivityStatusBadge.vue"
import { markRaw } from "vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import draggable from "vuedraggable"
import { useI18n } from "vue-i18n"

export default {
  components: {
    Tally2,
    draggable,
    Check,
    Plus,
    TagsAutoCompleteInput,
    TextEditor,
    RotateCw,
    SelectTemplateDropDownMenu,
    DateTimeFrame,
    Badge: markRaw(Badge),
    ClipboardCheck: markRaw(ClipboardCheck),
    AttachmentsField,
    Pagination,
    X,
    Maximize2,
    AsyncAutoCompleteInput,
    ChevronRight,
    ChevronsRight,
    Modal,
    TextInput,
    DatePicker,
    SelectInput,
    Pencil,
    Trash2,
    Form,
    ContactsSelect,
    CompaniesSelect,
    TeamMembersSelect,
    NotebookPen,
    Menu,
    MenuButton,
    MenuItems,
    ToggleStatusButton,
    Avatar,
    Switch
  },
  props: {
    isEditTemplate: {
      type: Boolean,
      default: false
    },
    selectedTemplate: {
      type: Object,
      default: null
    },
    autocompleteOptionsInPlaybook: {
      type: Array,
      required: false,
      default: () => []
    },
    resultReferencesList: null,
    resultReferencesListForText: null,
    playbookObjectType: null,
    inPlaybook: {
      type: Boolean,
      default: false
    },
    records: {
      type: Array,
      default: () => []
    },
    record: {
      type: Object,
      required: false
    },
    fullScreen: {
      type: Boolean,
      default: false
    },
    fromExpand: {
      type: Boolean,
      default: false
    },
    contact: {
      type: Object,
      required: false
    },
    isAllRecords: {
      type: Boolean,
      default: false
    },
    excludedRecords: {
      type: Array,
      default: () => []
    },
    objectType: {
      type: String,
      required: false
    },
    count: null,
    playbookOptions: {
      type: Array,
      default: () => [],
      required: false
    }
  },

  emits: ["cancel", "submitted", "error", "done", "updateValues"],
  setup(props) {
    const store = useStore()
    const { t } = useI18n()
    const schema = Yup.object().shape({
      name: Yup.string().required(t("Plan name is required")),
      description: Yup.string(),
      goal: Yup.string().nullable(),
      assigned_to: Yup.array().of(Yup.string()),
      type: Yup.string().required(t("Plan type is required"))
    })

    let name = ""
    if (props.fromExpand) {
      name = store.state.data.plan_data.plan_name
    }

    let type = ""
    if (props.fromExpand) {
      type = store.state.data.plan_data.plan_type
    }

    let description = ""
    if (props.fromExpand) {
      description = store.state.data.plan_data.plan_description
    }

    const initial_values = { name, description, type }

    return {
      schema,
      initial_values
    }
  },

  data() {
    return {
      assign_collaborators_to_goals_and_tasks: false,
      auto_set_start_date: false,
      list_to_get: "goals",
      list_to_get_task: "tasks",
      selected_tags: [],
      all_tags: [],
      is_tags_modal_open: false,
      assign_companies_to_goals_and_tasks: false,
      is_edit_template: false,
      task_icon: ClipboardCheck,
      Record,
      attachments: [],
      note_hover_index: null,
      submitting_is_on: false,
      expanded_goals: [],
      expanded_tasks: [],
      notes: [],
      is_any_nested_modal_open: false,
      isBoxBackground: false,
      types: [
        { label: this.$t("Account Plan"), value: "account_plan" },
        { label: this.$t("Growth Plan"), value: "growth_plan" },
        { label: this.$t("Success Plan"), value: "success_plan" },
        { label: this.$t("Onboarding Plan"), value: "onboarding_plan" },
        { label: this.$t("Health Plan"), value: "health_plan" },
        { label: this.$t("Renewal Plan"), value: "renewal_plan" },
        { label: this.$t("Other"), value: "other" }
      ],
      selected_template: null,
      templates: [],
      note_to_be_deleted: null,
      note_to_be_edited: null,
      is_delete_note_modal_open: false,
      note_title: null,
      note_text: null,
      is_adding_note: false,
      is_editing_note: false,
      selected_companies: [],
      selected_team_members: [],
      selected_contacts: [],
      due_date: null,
      relative_date: null,
      relative_date_text: null,
      start_date: null,
      inputDueDateIsFocused: false,
      inputStartDateIsFocused: false,
      is_modal_relative_date_open: false,
      assign_companies_to_goal_tasks_modal: false,
      prevent_auto_add_company: 0,
      displayed_tasks: [],
      selected_tasks: [],
      selected_goals: [],
      created_goals: [],
      tasks_to_be_created: [],
      goal_hover_id: null,
      task_hover_id: null,

      goalsCurrentPage: 1,
      goalsPerPage: 6,

      tasksCurrentPage: 1,
      tasksPerPage: 6,

      notesCurrentPage: 1,
      notesPerPage: 3,
      notes_error_msg: "",
      auto_compute_status: true,

      plan_name: null,
      plan_description: null,
      plan_type: null
    }
  },

  computed: {
    page_show_options() {
      const options = [6]
      if (this.selected_goals.length > 6) {
        options.push(8)
      }
      if (this.selected_goals.length > 8) {
        options.push(10)
      }
      if (this.selected_goals.length > 10) {
        options.push(12)
      }
      return options
    },

    tasks_page_show_options() {
      const options = [6]
      if (this.selected_tasks.length > 6) {
        options.push(8)
      }
      if (this.selected_tasks.length > 8) {
        options.push(10)
      }
      if (this.selected_tasks.length > 10) {
        options.push(12)
      }
      return options
    },

    notes_page_show_options() {
      const options = [3]
      if (this.notes?.length > 3) {
        options.push(6)
      }
      if (this.notes?.length > 6) {
        options.push(9)
      }
      if (this.notes?.length > 9) {
        options.push(12)
      }
      if (this.notes?.length > 12) {
        options.push(15)
      }
      return options
    },

    goalsTotalPages() {
      return Math.ceil(this.selected_goals.length / this.goalsPerPage)
    },
    notesTotalPages() {
      return Math.ceil(this.notes?.length / this.notesPerPage)
    },
    tasksTotalPages() {
      return Math.ceil(this.selected_tasks.length / this.tasksPerPage)
    },

    goalsCurrentPageItems() {
      const start = (this.goalsCurrentPage - 1) * this.goalsPerPage
      return this.selected_goals.slice(start, start + this.goalsPerPage)
    },

    tasksCurrentPageItems() {
      const start = (this.tasksCurrentPage - 1) * this.tasksPerPage
      return this.selected_tasks.slice(start, start + this.tasksPerPage)
    },

    notesCurrentPageItems() {
      const start = (this.notesCurrentPage - 1) * this.notesPerPage
      return this.notes?.slice(start, start + this.notesPerPage)
    }
  },

  watch: {
    tasksCurrentPageItems: {
      handler() {
        const start = (this.tasksCurrentPage - 1) * this.tasksPerPage
        this.displayed_tasks = this.selected_tasks.slice(start, start + this.tasksPerPage)
      }
    },
    goalsPerPage: {
      handler() {
        this.goalsCurrentPage = 1
      }
    },
    tasksPerPage: {
      handler() {
        this.tasksCurrentPage = 1
      }
    },
    notesPerPage: {
      handler() {
        this.notesCurrentPage = 1
      }
    },
    relative_date() {
      if (this.relative_date) {
        if (this.relative_date.due_date_relative_to == "datetime_created") {
          this.relative_date_text = `${this.relative_date.due_date_days_offset} ` + this.$t("days after Creation Date")
        } else if (this.relative_date.due_date_relative_to == "start_date") {
          this.relative_date_text = `${this.relative_date.due_date_days_offset} ` + this.$t("days after Start Date")
        } else {
          this.relative_date_text = `${this.relative_date.due_date_days_offset} ` + this.$t("days from In Progress")
        }
        this.due_date = null
      }
    },
    due_date() {
      if (this.due_date) {
        this.relative_date_text = null
      }
    },
    selected_team_members() {
      this.selected_goals.forEach((goal) => {
        goal.assigned_to = this.selected_team_members
      })
      this.selected_tasks.forEach((task) => {
        task.assigned_to = this.selected_team_members
      })
    },
    selected_template() {
      if (this.is_edit_template) {
        this.plan_name = this.selected_template.name
      } else {
        this.plan_name = this.selected_template.content.name
      }
      this.assign_companies_to_goals_and_tasks = this.selected_template?.content?.assign_companies_to_goals_and_tasks
      this.assign_collaborators_to_goals_and_tasks = this.selected_template?.content?.assign_collaborators_to_goals_and_tasks
      this.relative_date_text = null
      if (this.selected_template?.content?.due_date_relative_to) {
        this.relative_date = {
          due_date_relative_to: this.selected_template.content.due_date_relative_to,
          due_date_days_offset: this.selected_template.content.due_date_days_offset
        }
      } else {
        this.due_date = this.selected_template?.content?.due_date
      }
      if (this.selected_template?.content?.auto_start_date_set) {
        this.auto_set_start_date = this.selected_template?.content?.auto_start_date_set

        if (this.selected_template?.content.status == "in-progress" && !this.selected_template?.content.start_date) {
          this.start_date = new Date()
        }
      }

      this.start_date = this.selected_template?.content?.start_date
      if (this.selected_template?.content?.attachments?.length) {
        this.attachments = this.selected_template?.content?.attachments
      }
      if (this.selected_template?.content?.collaborators?.length) {
        this.selected_team_members = this.selected_template?.content?.collaborators
      }

      this.prevent_auto_add_company += 1
      if (this.selected_template?.content?.companies?.length) {
        this.selected_template?.content?.companies.forEach((c) => {
          if (c.id) {
            this.selected_companies.push(c)
          } else {
            this.$store.dispatch("data/getRecord", c).then((res) => {
              this.selected_companies.push(res.data)
            })
          }
        })
      }
      if (this.selected_template?.content?.contacts?.length) {
        this.selected_template?.content?.contacts.forEach((c) => {
          if (c.id) {
            this.selected_contacts.push(c)
          } else {
            this.$store.dispatch("data/getRecord", c).then((res) => {
              this.selected_contacts.push(res.data)
            })
          }
        })
      }

      this.selected_goals = []
      this.selected_tasks = []
      this.created_goals = []
      this.tasks_to_be_created = []
      this.selected_template?.content?.goals.map((goal) => {
        goal["assigned_to"] = this.selected_team_members.map((x) => (x.id ? x.id : x))
        delete goal?.assigned_to
        delete goal?.attachments
        delete goal?.notes
        goal["id"] = this.$uuid()
        goal["for_template"] = true
        this.selected_goals.push(goal)
      })
      this.selected_template?.content?.tasks.map((task) => {
        task["assigned_to"] = this.selected_team_members.map((x) => (x.id ? x.id : x))
        delete task?.attachments
        delete task?.notes
        task["id"] = this.$uuid()
        task["for_template"] = true
        this.selected_tasks.push(task)
      })
      this.notes = this.selected_template?.content?.notes
      if (
        this.prevent_auto_add_company !== 1 &&
        this.selected_companies.length &&
        (this.selected_template?.content?.goals?.length || this.selected_template?.content?.tasks?.length)
      ) {
        //this.assign_companies_to_goal_tasks_modal = true
      }
      if (this.selected_template?.content?.tags && this.selected_template?.content?.tags.length) {
        this.selected_tags = this.$store.state.data.actions_tags.filter((tag) => this.selected_template.content.tags.includes(tag.id))
      }
      this.plan_description = this.selected_template?.content?.description
    }
  },

  unmounted() {
    this.unregister_listeners()
  },
  deactivated() {
    this.unregister_listeners()
  },

  created() {
    if (this.selectedTemplate) {
      this.is_edit_template = this.isEditTemplate
      this.selected_template = this.selectedTemplate
      this.assign_companies_to_goals_and_tasks = this.selected_template.content.assign_companies_to_goals_and_tasks
      this.assign_collaborators_to_goals_and_tasks = this.selected_template.content.assign_collaborators_to_goals_and_tasks
    }
    const params = { source_object_type: "plan" }
    dataService.getTemplates(params).then((res) => {
      this.templates = res.data.results
    })
    this.register_listeners()

    if (!this.isEditTemplate) {
      this.selected_team_members.push(this.$store.state.auth.user)
    }

    this.selected_contacts = this.contact ? [this.contact] : []

    if (this.records.length > 0) {
      this.records.slice(0, 20).map((company_id) => {
        if (this.$store.state.data.records_fetched[company_id]) {
          this.selected_companies.push(this.$store.state.data.records_fetched[company_id])
        } else {
          this.$store.dispatch("data/getRecord", company_id).then((res) => {
            this.selected_companies.push(res.data)
          })
        }
      })
    }

    if (this.record) {
      if (this.record.id) {
        this.selected_companies.push(this.record)
      } else {
        if (this.$store.state.data.records_fetched[this.record]) {
          this.selected_companies.push(this.$store.state.data.records_fetched[this.record])
        } else {
          this.$store.dispatch("data/getRecord", this.record).then((res) => {
            this.selected_companies.push(res.data)
          })
        }
      }
    }

    if (this.$route.name == "WorkspaceItem") {
      this.$store.dispatch("data/getRecord", this.$store.state.ui.currentWorkspace.record).then((res) => {
        this.selected_companies.push(res.data)
      })
    }
    this.populatePlanData()
  },
  activated() {
    this.register_listeners()
  },

  methods: {
    emptyStartDate() {
      this.start_date = null
      this.$refs.start_date.hidePopover()
    },
    emptyDueDate() {
      this.due_date = null
      this.relative_date = null
      this.relative_date_text = null
      this.$refs.due_date.hidePopover()
      this.is_modal_relative_date_open = false
    },

    getActualStatus(object, status) {
      if (object.due_date) {
        if (
          (status == "pending" || status == "in-progress") &&
          this.$moment(object.due_date).local().startOf("day").isBefore(this.$moment().startOf("day"))
        ) {
          return "overdue"
        }
        return status
      }
      return status
    },
    handleStatusChanged(status, item) {
      item.status = status
    },

    handleTagsChanged(tags) {
      this.all_tags = this.$store.state.data.actions_tags
      this.selected_tags = tags
    },
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.CONFIRM_ADD_GOAL_TASKS_TO_PLAN_CREATE_FORM, (goals) => {
        const goal = goals[0]
        if (goal.id) {
          dataService.getGoalTasks(goal.id).then((res) => {
            const goal_tasks = res.data.results
            let page = 2
            this.onlyAddTasksNotInPlans(goal_tasks)
            if (goal.tasks.results?.length < goal.tasks.count) {
              const addUnaddedTasksToPlan = () => {
                const params = { page_size: 10, page: page }
                dataService.getGoalTasks(goal.id, params).then((res) => {
                  this.onlyAddTasksNotInPlans(res.data.results)

                  if (res.data.next) {
                    page += 1
                    addUnaddedTasksToPlan()
                  } else {
                    this.tasksChangePage(1, true)
                  }
                })
                this.tasksCurrentPage = 1
              }
              addUnaddedTasksToPlan()
            }
          })
        } else {
          const goal_tasks = goal.tasks.map((t) => {
            return { ...t, for_template: true, assigned_to: [], id: this.$uuid() }
          })
          this.onlyAddTasksNotInPlans(goal_tasks)
        }
      })
      this.$emitter.on(ALL_EMITTERS.ADD_SELECTED_GOALS_TO_PLAN_CREATE_FORM, (goals) => {
        this.handleGoalSelected(null, () => {}, goals)

        this.goalsCurrentPage = 1
      })
      this.$emitter.on(ALL_EMITTERS.ADD_SELECTED_TASKS_TO_PLAN_CREATE_FORM, (tasks) => {
        this.selected_tasks = tasks.concat(this.selected_tasks)
        this.tasksCurrentPage = 1
      })

      this.$emitter.on(ALL_EMITTERS.GOAL_UPDATED_IN_PLAN_CREATE_FORM, (goal) => {
        let index = this.selected_goals.findIndex((g) => g.id == goal.id)
        this.selected_goals.splice(index, 1, goal)
      })

      this.$emitter.on(ALL_EMITTERS.TASK_UPDATED_IN_PLAN_CREATE_FORM, (task) => {
        let index = this.selected_tasks.findIndex((g) => g.id == task.id)
        this.selected_tasks.splice(index, 1, task)
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.CONFIRM_ADD_GOAL_TASKS_TO_PLAN_CREATE_FORM)
      this.$emitter.off(ALL_EMITTERS.ADD_SELECTED_GOALS_TO_PLAN_CREATE_FORM)
      this.$emitter.off(ALL_EMITTERS.ADD_SELECTED_TASKS_TO_PLAN_CREATE_FORM)
      this.$emitter.off(ALL_EMITTERS.GOAL_UPDATED_IN_PLAN_CREATE_FORM)
      this.$emitter.off(ALL_EMITTERS.TASK_UPDATED_IN_PLAN_CREATE_FORM)
    },
    endTaskDrag() {
      this.selected_tasks = this.$utils.clone(this.displayed_tasks)
    },
    populatePlanData() {
      if (this.fromExpand) {
        Object.keys(this.$data).forEach((key) => {
          this[key] = this.$store.state.data.plan_data[key]
        })
      }
    },
    onlyAddTasksNotInPlans(tasks) {
      tasks?.forEach((task) => {
        let index = this.selected_tasks.findIndex((selected_task) => selected_task.id == task.id)
        if (index == -1) {
          this.selected_tasks.unshift(task)
        }
      })
    },
    handleEditNoteText(e) {
      this.note_text = e.replace(/href="(?!http)/, 'href="http://')
    },
    goalsChangePage(page) {
      this.goalsCurrentPage = page
    },
    tasksChangePage(page) {
      this.tasksCurrentPage = page
    },
    notesChangePage(page) {
      this.notesCurrentPage = page
    },

    handleGoalEnter(goal) {
      this.$utils.debounce(() => {
        this.goal_hover_id = goal.id
      }, 600)
    },
    handleGoalLeave() {
      this.goal_hover_id = null
      this.$utils.debounce(() => {
        this.goal_hover_id = null
      }, 600)
    },

    handleTaskEnter(task) {
      this.$utils.debounce(() => {
        this.task_hover_id = task.id
      }, 600)
    },
    handleTaskLeave() {
      this.task_hover_id = null
      this.$utils.debounce(() => {
        this.task_hover_id = null
      }, 600)
    },

    handleGoalSelected(goal, close, goals) {
      if (goal?.content) {
        goal = goal.content

        goal.for_template = true

        if (goal?.id) {
          goal["id"] = this.$uuid()
        }
      }

      let tasks_count = 0
      const goals_with_tasks = []
      let multiple_goals = false
      if (goal && goal?.tasks?.count) {
        tasks_count = goal.tasks.count
        goals_with_tasks.push(goal)
      } else if (goal?.tasks?.length) {
        tasks_count = goal.tasks.length
        goals_with_tasks.push(goal)
      } else if (goals?.length) {
        multiple_goals = true
        goals.forEach((goal) => {
          tasks_count = 0
          goals_with_tasks.push(goal)
          if (goal && goal.tasks?.count) {
            tasks_count += goal.tasks.count
          }
        })
      }
      if (tasks_count) {
        this.$emitter.emit(ALL_EMITTERS.ADD_GOAL_TASKS_TO_PLAN, {
          tasks_count,
          multiple_goals,
          goals_with_tasks
        })
      }

      if (goals?.length) {
        goals.forEach((goal) => {
          const goal_modified = {
            ...goal,
            filter: goal.filter?.split(" ")[0]
              ? goal.filter.split(" ")[0].length > 1
                ? `( ${goal.filter.slice(1, goal.filter.length - 1)} )`
                : goal.filter
              : goal.filter
          }
          this.selected_goals.unshift(goal_modified)
        })
      } else {
        const goal_modified = {
          ...goal,
          filter: goal.filter?.split(" ")[0]
            ? goal.filter.split(" ")[0].length > 1
              ? `( ${goal.filter.slice(1, goal.filter.length - 1)} )`
              : goal.filter
            : goal.filter
        }
        this.selected_goals.unshift(goal_modified)
      }

      close()
    },

    // handleGoalSelected(goal, close) {
    //   const goal_modified = { ...goal, filter: goal.filter?.split(" ")[0] ? (goal.filter.split(" ")[0].length > 1 ? `( ${goal.filter.slice(1, goal.filter.length - 1)} )` : goal.filter) : goal.filter }
    //   this.selected_goals.unshift(goal_modified)
    //   this.goalsCurrentPage = 1
    //   close()
    // },

    handleTaskSelected(task, close) {
      if (task.content) {
        task = task.content
        task.for_template = true
        if (task?.id) {
          task["id"] = this.$uuid()
        }
      }

      this.selected_tasks.unshift(task)
      this.tasksCurrentPage = 1
      close()
    },
    handleCreateGoal() {
      this.is_any_nested_modal_open = true
      this.$emitter.emit(ALL_EMITTERS.CREATE_GOAL, {
        inPlan: true,
        records: this.selected_companies,
        closeCallBack: () => {
          this.is_any_nested_modal_open = false
        }
      })
    },
    handleCreateTask() {
      this.is_any_nested_modal_open = true
      this.$emitter.emit(ALL_EMITTERS.CREATE_TASK, {
        inPlan: true,
        records: this.selected_companies,
        closeCallBack: () => {
          this.is_any_nested_modal_open = false
        }
      })
    },
    handleExpandGoal(goal) {
      if (!goal.id) {
        goal["id"] = this.$uuid()
      }
      let index = this.expanded_goals.findIndex((g) => g == goal.id)
      if (index !== -1) {
        this.expanded_goals.splice(index, 1)
      } else {
        this.expanded_goals.push(goal.id)
      }
    },

    handleExpandTask(task) {
      let index = this.expanded_tasks.findIndex((t) => t == task.id)
      if (index !== -1) {
        this.expanded_tasks.splice(index, 1)
      } else {
        this.expanded_tasks.push(task.id)
      }
    },
    handleViewGoal(goal) {
      const goal_copy = this.$utils.clone(goal)
      this.$emitter.emit(ALL_EMITTERS.UPDATE_GOAL, {
        goal: goal_copy,
        is_template: this.is_edit_template
      })
    },
    handleViewTask(task) {
      const task_copy = this.$utils.clone(task)
      this.$emitter.emit(ALL_EMITTERS.UPDATE_TASK, {
        task: this.$utils.clone(task_copy),
        is_template: this.is_edit_template
      })
    },
    handleRemoveGoal(goal) {
      let index = this.selected_goals.findIndex((g) => g.id == goal.id)
      this.selected_goals.splice(index, 1)
      let index_2 = this.expanded_goals.findIndex((goal) => goal == goal.id)
      this.expanded_goals.splice(index_2, 1)
      let index_3 = this.created_goals.findIndex((t) => t.id == goal.id)
      if (index_3 != -1) {
        if (!this.inPlaybook) {
          this.$store.dispatch("data/removeGoal", {
            task: this.created_goals[index_3]
          })
        }
        this.created_goals.splice(index_3, 1)
      }

      const total_pages = Math.ceil(this.selected_goals.length / this.goalsPerPage)

      if (this.goalsCurrentPage > total_pages) {
        this.goalsCurrentPage = total_pages
      }
    },
    handleRemoveTask(task) {
      let index = this.selected_tasks.findIndex((g) => g.id == task.id)
      this.selected_tasks.splice(index, 1)
      let index_2 = this.expanded_tasks.findIndex((task) => task == task.id)
      this.expanded_tasks.splice(index_2, 1)

      const total_pages = Math.ceil(this.selected_tasks.length / this.tasksPerPage)

      if (this.tasksCurrentPage > total_pages) {
        this.tasksCurrentPage = total_pages
      }
    },
    handleExpandPlan() {
      if (this.fullScreen) {
        this.$router.go(-1)
        this.$emitter.emit(ALL_EMITTERS.CREATE_PLAN, { from_expand: true })
      } else {
        this.$router.push(`/plans/new`)
        this.$emit("cancel")
      }

      this.$store.dispatch("data/setPlanExpandData", { ...this.$data })
    },

    handleClosePlan() {
      this.$emit("cancel")
    },

    handleSetConmpaniesList(e) {
      this.selected_companies = e
    },

    handleAddNote() {
      this.is_adding_note = true
      this.note_title = null
      this.note_text = null
    },

    editNote(note) {
      this.note_to_be_edited = note
      this.is_editing_note = true
      this.note_title = note.title
      this.note_text = note.description
    },
    deleteNote(note) {
      this.note_to_be_deleted = note
      this.is_delete_note_modal_open = true
    },
    confirmDeleteNote(showIsDone) {
      this.notes?.splice(this.note_to_be_deleted.id, 1)
      showIsDone()

      const total_pages = Math.ceil(this.notes?.length / this.notesPerPage)

      if (this.notesCurrentPage > total_pages) {
        this.notesCurrentPage = total_pages
      }
    },
    saveNote() {
      this.notes_error_msg = ""
      if (!this.note_title) {
        this.notes_error_msg = this.$t("note title missing")
      } else if (!this.note_text) {
        this.notes_error_msg = this.$t("note description missing")
      } else {
        if (this.is_adding_note) {
          this.notes?.unshift({
            title: this.note_title,
            description: this.note_text
          })
          this.is_adding_note = false
          this.notesCurrentPage = 1
        } else if (this.is_editing_note) {
          this.notes?.map((note, i) => {
            if (note === this.note_to_be_edited) {
              const note_copy = { ...note }
              note_copy.title = this.note_title
              note_copy.description = this.note_text

              this.notes?.splice(i, 1, note_copy)
            }
          })
          this.is_editing_note = false
        }
      }
    },
    focusStartDateInput() {
      this.inputStartDateIsFocused = !this.inputStartDateIsFocused
    },
    focusDueDateInput() {
      this.inputDueDateIsFocused = !this.inputDueDateIsFocused
    },
    clearDate() {
      this.$refs.start_date.hidePopover()
    },
    createPlan(values) {
      let data = { ...values }
      this.submitting_is_on = true
      data["organization"] = this.$store.state.organization.id
      data["companies"] = this.selected_companies.map((x) => (x.id ? x.id : x))
      data["collaborators"] = this.selected_team_members.map((x) => (x.id ? x.id : x))
      data["contacts"] = this.selected_contacts.map((x) => x.id)
      data["notes"] = this.notes?.filter((n) => n.id)
      data["show_dynamic_status"] = this.auto_compute_status
      data["is_all_records"] = this.isAllRecords
      data["description"] = this.plan_description
      data["tags"] = this.selected_tags.map((t) => t.id)
      data["excluded_records"] = this.excludedRecords
      data["object_type"] = this.objectType
      data["assign_companies_to_goals_and_tasks"] = this.assign_companies_to_goals_and_tasks
      data["assign_collaborators_to_goals_and_tasks"] = this.assign_collaborators_to_goals_and_tasks
      data["auto_start_date_set"] = this.auto_set_start_date

      data["auto_start_date_set"] = this.auto_set_start_date

      if (this.start_date) {
        data["start_date"] = this.$moment(this.start_date)
          .set("hour", this.$moment().hour())
          .set("minute", this.$moment().minute())
          .set("second", this.$moment().second())
          .toISOString()
      } else {
        data["start_date"] = null
      }
      if (this.relative_date) {
        data["due_date_relative_to"] = this.relative_date.due_date_relative_to
        data["due_date_days_offset"] = this.relative_date.due_date_days_offset
      } else if (this.due_date) {
        data["due_date"] = this.$moment(this.due_date)
          .set("hour", this.$moment().hour())
          .set("minute", this.$moment().minute())
          .set("second", this.$moment().second())
          .toISOString()
      } else {
        data["due_date"] = null
        data["due_date_relative_to"] = null
        data["due_date_days_offset"] = 0
      }

      if (this.is_edit_template) {
        //Edit template
        this.notes?.map((note) => {
          note["organization"] = this.$store.state.organization.id
        })
        // data["notes"] = this.notes
        this.selected_tasks.map((task) => {
          // this.$store.dispatch("data/removeTask", { task: task })
          delete task["id"]
          task["assigned_to"] = [this.$store.state.auth.user.id]
          task["attachments"] = []
          task["contacts"] = []
          task["tags"] = []
        })
        this.selected_goals.map((goal) => {
          // this.$store.dispatch("data/removeGoal", { goal: goal })
          delete goal["id"]
          goal["assigned_to"] = [this.$store.state.auth.user.id]
          goal["attachments"] = []
          goal["tasks"] = []
          goal["notes"] = []
          goal["tags"] = []
        })
        data["tasks"] = this.selected_tasks.map((task) => {
          return { ...task, record: task.record ? (task.record.id ? task.record.id : task.record) : null }
        })
        data["goals"] = this.selected_goals.map((goal) => {
          return { ...goal, record: goal.record ? (goal.record?.id ? goal.record?.id : goal.record) : null }
        })

        let data_template = { ...this.selected_template.content, ...data }
        dataService
          .updateTemplate(this.selected_template.id, {
            name: this.plan_name,
            content: data_template
          })
          .then((res) => {
            this.$emitter.emit(ALL_EMITTERS.TEMPLATE_UPDATED, res.data)
            this.$emit("done")
          })
      } else {
        //Create new plan
        this.tasks_to_be_created = []
        data["tasks"] = []
        this.selected_tasks.map((t) => {
          if (t?.id && !t.for_template) {
            if (this.assign_companies_to_goals_and_tasks || this.assign_collaborators_to_goals_and_tasks) {
              let post = {
                task: { id: t.id }
              }
              if (this.assign_companies_to_goals_and_tasks && this.selected_companies.length) {
                post["task"]["record"] = this.selected_companies[0].id
              }
              if (this.assign_collaborators_to_goals_and_tasks && this.selected_team_members.length) {
                post["task"]["assigned_to"] = this.selected_team_members.map((x) => (x.id ? x.id : x))
              }
              this.$store.dispatch("data/updateTask", post)
            }
            data.tasks.push(t)
          } else {
            delete t.id
            this.tasks_to_be_created.push(t)
          }
        })
        data["goals"] = []
        this.selected_goals.map((g) => {
          if (g?.id && !g.for_template) {
            if (this.assign_companies_to_goals_and_tasks || this.assign_collaborators_to_goals_and_tasks) {
              let post = {
                goal: { id: g.id }
              }
              if (this.assign_companies_to_goals_and_tasks && this.selected_companies.length) {
                post["goal"]["record"] = this.selected_companies[0].id
              }
              if (this.assign_collaborators_to_goals_and_tasks && this.selected_team_members.length) {
                post["goal"]["assigned_to"] = this.selected_team_members.map((x) => (x.id ? x.id : x))
              }
              this.$store.dispatch("data/updateGoal", post)
            }
            data.goals.push(g)
          } else {
            delete g.id
            this.created_goals.push(g)
          }
        })

        if (this.inPlaybook) {
          if (this.selected_template) {
            data["template_id"] = this.selected_template.id
          }

          if (!data["due_date_relative_to"]) {
            delete data["due_date_relative_to"]
          }
          data["assign_companies_to_goals_and_tasks"] = this.assign_companies_to_goals_and_tasks
          data["assign_collaborators_to_goals_and_tasks"] = this.assign_collaborators_to_goals_and_tasks
          data["status"] = "pending"

          const clean_goals = []
          const clean_tasks = []
          this.selected_goals.forEach((g) => {
            const goal_clone = this.$utils.clone(g)

            goal_clone["assigned_to"] = goal_clone["assigned_to"]?.map((g) => (g.value ? g.value : g.id ? g.id : g)) || []
            goal_clone["record"] = goal_clone["record"]?.id ? goal_clone["record"].id : goal_clone["record"]

            if (!goal_clone["due_date_relative_to"]) {
              delete goal_clone["due_date_relative_to"]
            }

            if (goal_clone["tasks"]?.results) {
              goal_clone["tasks"] = goal_clone["tasks"].results
            }

            if (goal_clone["attachments"]?.results) {
              goal_clone["attachments"] = goal_clone["attachments"].results
            }

            if (!goal_clone["record"]) {
              delete goal_clone["record"]
            }

            if (!goal_clone["filter"]) {
              delete goal_clone["filter"]
            }

            if (goal_clone["notes"]?.results) {
              goal_clone["notes"] = goal_clone["notes"].results
            }

            goal_clone["tasks"] = []

            if (goal_clone.for_template || this.selected_template) {
              delete goal_clone.id

              if (this.assign_companies_to_goals_and_tasks) {
                if (data?.companies.length) {
                  goal_clone["record"] = this.selected_companies.map((x) => (x.id ? x.id : x))[0]
                }

                if (data?.collaborators?.length) {
                  goal_clone["collaborators"] = this.selected_team_members.map((x) => (x.id ? x.id : x))
                }
              }

              clean_goals.push(goal_clone)
            } else {
              clean_goals.push(goal_clone.id)
            }
          })

          this.selected_tasks.forEach((t) => {
            const task_clone = this.$utils.clone(t)

            task_clone["assigned_to"] = task_clone["assigned_to"]?.map((t) => (t.value ? t.value : t.id ? t.id : t)) || []
            task_clone["record"] = task_clone["record"]?.id ? task_clone["record"].id : task_clone["record"]

            if (!task_clone["due_date_relative_to"]) {
              delete data["due_date_relative_to"]
            }

            if (!task_clone["due_date_relative_to"]) {
              delete task_clone["due_date_relative_to"]
            }

            if (task_clone["attachments"]?.results) {
              task_clone["attachments"] = task_clone["attachments"].results
            }

            if (!task_clone["record"]) {
              delete task_clone["record"]
            }

            if (task_clone["notes"]?.results) {
              task_clone["notes"] = task_clone["notes"].results
            }

            if (task_clone.for_template || this.selected_template) {
              delete task_clone.id

              if (this.assign_companies_to_goals_and_tasks) {
                if (data?.companies.length) {
                  task_clone["record"] = this.selected_companies.map((x) => (x.id ? x.id : x))[0]
                }

                if (data?.collaborators?.length) {
                  task_clone["collaborators"] = this.selected_team_members.map((x) => (x.id ? x.id : x))
                }
              }

              clean_tasks.push(task_clone)
            } else {
              clean_tasks.push(task_clone.id)
            }
          })

          data["goals"] = clean_goals
          data["tasks"] = clean_tasks

          if (!data["due_date_relative_to"]) {
            delete data["due_date_relative_to"]
          }

          if (this.attachments.length) {
            const new_attachments = this.attachments.filter((att) => typeof att.file !== "string")
            const old_attachments = this.attachments.filter((att) => typeof att.file == "string")

            let attachment_calc = old_attachments.map((att) => att.id).filter((a) => a)

            if (new_attachments.length) {
              dataService.addAttachment({ attachments: new_attachments }).then((res) => {
                attachment_calc = attachment_calc.concat(res.data.map((a) => a.id))
                data["attachments"] = attachment_calc
                this.$emit("done", data)
              })
            } else {
              data["attachments"] = attachment_calc
              this.$emit("done", data)
            }
          } else {
            this.$emit("done", data)
          }
          google_event("create_plan_click", {
            event_label: "Create new plan",
            page: "Playbooks",
            name: "Playbooks",
            user_name: this.$store.state.auth.user.full_name,
            user_email: this.$store.state.auth.user.email,
            organization: this.$store.state.organization.name
          })
        } else {
          data["tasks"] = data.tasks
            .map((t) => {
              return { id: t.id, name: t.name }
            })
            .concat(this.tasks_to_be_created)
          data["goals"] = data.goals
            .map((g) => {
              return { id: g.id, name: g.name }
            })
            .concat(this.created_goals)
          this.$store
            .dispatch("data/createPlan", { plan: data })
            .then((res) => {
              if (this.attachments.length) {
                let formData = new FormData()
                this.attachments.forEach((attachment, i) => {
                  Object.keys(attachment).forEach((field) => formData.append(`attachments[${i}][${field}]`, attachment[field]))
                })
                const params = {
                  goals_page_size: 6,
                  tasks_page_size: 6,
                  notes_page_size: 3
                }
                this.$store.dispatch("data/addAttachmentsToPlan", {
                  id: res.id,
                  attachments: formData,
                  params
                })
              }
              this.$emitter.emit(ALL_EMITTERS.NEW_ACTINON_CREATED_OR_UPDATED, res.due_date ? new Date(res.due_date) : null)
              this.submitting_is_on = false
              this.$emit("done", res)
            })
            .catch(() => {
              this.submitting_is_on = false
              // To do show exceptions
              this.$emit("error")
            })
        }
      }
    }
  }
}
</script>
